import { FunctionComponent, PropsWithChildren, ReactNode } from 'react'
import styled from 'styled-components'

import ReviewScore from 'components/reviews/ReviewScore'
import InvisibleButton from '../../display/InvisibleButton'
import ResponsiveImage from '../../display/ResponsiveImage'
import type { ResponsiveImageProperties } from '../../display/ResponsiveImage/types'

import LinkOrA from '../shared/LinkOrA'
import { MediaWrapperOrientated } from '../shared/MediaWrapper'
import {
  CardImageOverlay,
  OverlayChips,
  OverlayBucketListButton,
} from '../shared/CardImageOverlay'
import { CardWrapper, Content, Location, Title } from '../shared/styled'
import CardSlider from '../shared/CardSlider'

const AdventureCardWrapper = styled(CardWrapper)`
  white-space: normal;
`

const ButtonWrapper = styled(InvisibleButton)`
  text-align: left;
  outline: none;
  width: 100%;
`

const ActionsWrapper = styled.div`
  margin-left: auto;
`

const AdventureInfoWrapper = styled.div`
  padding-right: 16px;
`

interface LinkOrButtonProps {
  url: string
  onClick?: (...args: any) => any
  asLink?: boolean
}

const LinkOrButton: FunctionComponent<PropsWithChildren<LinkOrButtonProps>> = ({
  onClick,
  url,
  children,
  asLink,
}) =>
  onClick ? (
    <ButtonWrapper onClick={onClick}>{children}</ButtonWrapper>
  ) : (
    <LinkOrA to={url} asLink={asLink}>
      {children}
    </LinkOrA>
  )

type StorytellerType = Pick<GQL_User, 'id' | 'firstName'> & {
  avatar: Pick<GQL_UserAvatar, 'thumbLg'>
}
type Photo = {
  image: ResponsiveImageProperties
}

type AdventureCardProps = {
  title: string
  photos: Photo[]
  storyteller: StorytellerType
  location: {
    name?: string
    lat: number
    lng: number
  }
  reviewScore: string
  onClick?: (...args: any) => any
  onSelectEscapeClick?: () => any
  asLink?: boolean
  showReviewScore?: boolean
  isInEscape?: boolean
  url: string
  actions?: ReactNode
  compactCard?: boolean
}

const AdventureCard = ({
  title,
  photos,
  storyteller,
  location,
  onClick,
  onSelectEscapeClick,
  asLink,
  isInEscape,
  reviewScore,
  showReviewScore,
  url,
  actions,
  compactCard,
}: AdventureCardProps) => {
  const adventureScore = parseFloat(reviewScore)

  return (
    <AdventureCardWrapper>
      <MediaWrapperOrientated radius="md">
        {photos && photos.length > 1 ? (
          <CardSlider title={title} photos={photos} url={url} asLink={asLink} />
        ) : (
          photos?.[0] && (
            <LinkOrA to={url} asLink={asLink}>
              <ResponsiveImage {...photos[0].image} cover alt={title} />
            </LinkOrA>
          )
        )}
        <CardImageOverlay>
          <OverlayChips />
          <OverlayBucketListButton
            onClick={onSelectEscapeClick}
            isActive={isInEscape}
          />
        </CardImageOverlay>
      </MediaWrapperOrientated>

      <Content compact={compactCard}>
        {storyteller && (
          <AdventureInfoWrapper>
            <LinkOrButton onClick={onClick} url={url} asLink={asLink}>
              <Title compact={compactCard}>{title}</Title>
              <Location compact={compactCard}>{location?.name}</Location>
              {showReviewScore && adventureScore > 0 && (
                <ReviewScore score={adventureScore} size="small" />
              )}
            </LinkOrButton>
          </AdventureInfoWrapper>
        )}

        <ActionsWrapper>{actions}</ActionsWrapper>
      </Content>
    </AdventureCardWrapper>
  )
}

export default AdventureCard
